import { withMountWhenInView } from '@studiometa/js-toolkit';
import { toggleClass } from '@studiometa/js-toolkit/utils';
import { Transition, loadImage } from '@studiometa/ui';

/**
 * VideoFigure class.
 */
export default class VideoFigure extends withMountWhenInView(Transition, {
  threshold: [0, 1],
}) {
  /**
   * Config.
   */
  static config = {
    ...Transition.config,
    name: 'VideoFigure',
    emits: ['load', 'mute', 'unmute'],
    refs: ['video', 'mute'],
    options: {
      ...Transition.config.options,
      lazy: Boolean,
      lazyPoster: Boolean,
      muted: Boolean,
      loaded: Boolean,
    },
  };

  /**
   * Target
   * @returns {HTMLVideoElement}
   */
  get target() {
    return this.$refs.video;
  }

  /**
   * Sources
   * @returns {Array}
   */
  get sources() {
    return Array.from(this.$refs.video.querySelectorAll('source'));
  }

  /**
   * Load poster
   * @returns {Promise}
   */
  loadPoster() {
    const { video } = this.$refs;

    if (!video.dataset.poster || !this.$options.lazyPoster) {
      return Promise.resolve();
    }

    return loadImage(video.dataset.poster).then(() => {
      video.poster = video.dataset.poster;
      this.$log('fresh poster loaded');
    });
  }

  /**
   * Load sources
   * @returns {Promise}
   */
  loadSources() {
    const { video } = this.$refs;

    this.sources.forEach((source) => {
      if (!source.dataset.src) {
        return;
      }
      source.src = source.dataset.src;
    });

    return new Promise((resolve) => {
      const loadHandler = () => {
        video.removeEventListener('loadeddata', loadHandler);
        this.$log('fresh sources loaded');
        resolve();
      };
      video.addEventListener('loadeddata', loadHandler);
      video.load();
    });
  }

  /**
   * Load
   * @returns {Promise}
   */
  load() {
    return Promise.all([this.loadPoster(), this.loadSources()]);
  }

  /**
   * Load on mount.
   */
  async mounted() {
    this.$log('mounted');
    const { video } = this.$refs;

    if (!video) {
      throw new Error('[VideoFigure] The `video` ref is required.');
    }

    if (!(video instanceof HTMLVideoElement)) {
      throw new Error('[VideoFigure] The `video` ref must be an `<video>` element.');
    }

    this.$options.muted = this.target?.muted;

    if (!this.$options.lazy) {
      this.$log('Lazy loading disabled');
      return;
    }

    if (this.$options.loaded) {
      this.$log('Already loaded');
      return;
    }

    this.$log('start loading');
    await this.load();
    this.$options.loaded = true;
    this.$log('all is loaded');
    await this.enter();
    this.$log('transition done');
    this.$emit('load');
  }

  /**
   * Mute
   */
  mute() {
    if (!this.target || (this.$options.lazy && !this.$options.loaded)) {
      return;
    }
    this.target.muted = true;
    this.$options.muted = true;
    toggleClass(document.documentElement, `is-video-unmuted-${this.$id}`, false);
    this.$emit('mute');
  }

  /**
   * Unmute
   */
  unmute() {
    if (!this.target || (this.$options.lazy && !this.$options.loaded)) {
      return;
    }
    this.target.muted = false;
    this.$options.muted = false;
    toggleClass(document.documentElement, `is-video-unmuted-${this.$id}`, true);
    this.$emit('unmute');
  }

  /**
   * Toggle mute
   */
  toggleMute() {
    if (!this.target || (this.$options.lazy && !this.$options.loaded)) {
      return;
    }
    if (this.target.muted) {
      this.unmute();
    } else {
      this.mute();
    }
  }

  /**
   * On audio controller click
   */
  onMuteClick() {
    this.toggleMute();
  }

  /**
   * Scrolled
   */
  scrolled() {
    this.mute();
  }
}
